import { useTranslation } from 'react-i18next';
import { ProfileDetails } from 'templates';
import { useEffect, useState, useContext, memo, useRef } from 'react';
import {
  apiResponseSafeCheck,
  usePreferenceSearchAPI,
  domainForSegment,
  ANALYTICS_MESSAGES,
  useAnalytics,
} from 'common';
import { GlobalContext } from 'context';
import { Headings, Loader, Link } from '@sentara-health/optimahealth_web';

const Profile = () => {
  const { serviceNumber, loginMemberId, profileState, setProfileState } =
    useContext(GlobalContext);
  const [phone, setPhone] = useState<any>({});
  const [changePassword, setChangePassword] = useState<boolean>(false);
  const { t } = useTranslation();
  const { trackContentListing } = useAnalytics();
  const [loader, setLoader] = useState(false);
  const effectRan = useRef(false);

  // Update phone state when serviceNumber changes
  useEffect(() => {
    if (apiResponseSafeCheck(serviceNumber)) {
      setPhone(JSON.parse(serviceNumber ?? '{}'));
    }
  }, [serviceNumber]);

  // Fetch profile data (member details and preference search) sequentially
  useEffect(() => {
    if (effectRan.current) return;
    if (loginMemberId !== null && loginMemberId !== '') {
      trackContentListing(
        '601',
        ANALYTICS_MESSAGES.PROFILE_SELECT,
        1,
        `${domainForSegment}/profile`
      );
      fetchProfileData();
      effectRan.current = true;
    }
  }, [loginMemberId]);

  const { fetchProfile, fetchMemberDetailsAPI } = usePreferenceSearchAPI();

  const fetchProfileData = async () => {
    setLoader(true);
    try {
      let profileData: any = {};

      // Fetch member details if firstName or lastName is missing
      if (
        profileState.firstName === '' ||
        profileState.lastName === '' ||
        profileState.firstName === undefined ||
        profileState.lastName === undefined
      ) {
        fetchMemberDetailsAPI();
      }
      // Fetch preference search data if member id is missing
      if (profileState.memberId === loginMemberId) {
        setProfileState((prevState: any) => ({
          ...prevState,
          ...profileData,
        }));
        setLoader(false);
      } else {
        await fetchProfile();
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      {/* Skip to Main Content link */}
      {loader && <Loader />}
      <Link to="#main-content" className="skip-link">
        {t('common.skipToMainContent')}
      </Link>

      <div>
        <main id="main-content">
          <div className="mb-4">
            {/* To view the profile heading */}
            <Headings
              level={1}
              text={t('common.profile')}
              variant="semiBold"
              dataTestId="userName"
            />
          </div>
          <ProfileDetails
            state={profileState} // Represents the profile state.
            setState={setProfileState} // Represents the function to set the profile state.
            getPhone={phone?.data} //Represents the phone data.
            changePassword={changePassword} // Represents the password change data.
            setChangePassword={setChangePassword} //Represents the function to set the password change data.
          />
        </main>
      </div>
    </>
  );
};

export default memo(Profile);
